import { generateKeywordsQueryForOr } from '@smarttransit/common-client'
import { findTransportationProfiles } from '../../services/transportation-profiles-service'
import { findTransportationOwnerProfiles } from '../../services/transportation-owners-service'
import { addAlert } from '../../utilities/helpers'

export default {
  name: 'transportation-profile-autocomplete',
  props: {
    value: {
      type: Object,
      default: null
    },
    label: {
      type: String,
      default: 'Search vehicle'
    },
    placeholder: {
      type: String,
      default: 'Filter by licence plate, driver name'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    transportationOwnerId: {
      type: Number,
      default: null
    },
    clearable: {
      type: Boolean,
      default: true
    },
    showDriver: {
      type: Boolean,
      default: true
    },
    showOwnerAccount: {
      type: Boolean,
      default: false
    },
    searchDelay: {
      type: Number,
      default: 800
    }
  },
  data () {
    return {
      isLoading: false,
      searchKey: '',
      selectedProfile: null,
      transportationProfiles: [],
      searchHandle: null
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler (newValue) {
        if (!newValue) {
          this.selectedProfile = null
          return
        }

        this.selectedProfile = newValue
        const exists = this.transportationProfiles.find((item) => item.id === newValue.id)
        if (!exists) {
          this.transportationProfiles = [newValue].concat(this.transportationProfiles)
        }
      }
    },
    selectedProfile (newValue) {
      this.$emit('input', newValue || null)
    },
    transportationOwnerId () {
      this.runSearch(this.searchKey)
    },
    searchKey (newValue) {
      this.runSearch(newValue)
    }
  },
  mounted () {
    this.runSearch(this.searchKey)
  },
  beforeDestroy () {
    clearTimeout(this.searchHandle)
  },
  methods: {
    buildFilter (keywords) {
      const include = []
      if (this.showDriver) {
        include.push({ currentDriverObj: 'stUser' })
      }

      const filter = {
        include,
        limit: 20
      }

      if (this.transportationOwnerId) {
        filter.join = {
          relation: 'transportationOwnerProfile',
          scope: { where: { transportationOwnerId: this.transportationOwnerId } }
        }
      }

      if (keywords) {
        const keywordFilter = generateKeywordsQueryForOr(keywords, ['licensePlateNumber', 'shortcodeId'])
        if (keywordFilter && keywordFilter.length) {
          filter.where = {
            or: keywordFilter
          }
        }
      }

      return filter
    },
    async enrichOwnerAccountNames (profiles) {
      if (!this.showOwnerAccount || !profiles.length) {
        return profiles
      }

      try {
        const profileIds = profiles.map((item) => item.id).filter(Boolean)
        if (!profileIds.length) {
          return profiles
        }

        const ownerProfiles = await findTransportationOwnerProfiles({
          where: { transportationProfileId: { inq: profileIds } },
          include: 'transportationOwner'
        })

        const ownerByProfileId = {}
        ;(ownerProfiles || []).forEach((item) => {
          ownerByProfileId[item.transportationProfileId] = item
        })

        return profiles.map((profile) => {
          const resolved = Object.assign({}, profile)
          const ownerProfile = ownerByProfileId[profile.id]
          resolved.ownerAccountName = ownerProfile && ownerProfile.transportationOwner
            ? ownerProfile.transportationOwner.companyName
            : ''
          return resolved
        })
      } catch (err) {
        return profiles
      }
    },
    runSearch (keywords) {
      clearTimeout(this.searchHandle)
      this.searchHandle = setTimeout(async () => {
        await this.searchTransportationProfiles(keywords)
      }, this.searchDelay || 800)
    },
    async searchTransportationProfiles (keywords) {
      this.isLoading = true
      try {
        const trimmedKeywords = String(keywords || '').trim()
        if (!trimmedKeywords && !this.transportationOwnerId) {
          this.transportationProfiles = this.selectedProfile ? [this.selectedProfile] : []
          return
        }

        const filter = this.buildFilter(trimmedKeywords)
        const profiles = await findTransportationProfiles(filter)
        const mappedProfiles = (profiles || []).map((item) => {
          const resolved = Object.assign({}, item)
          resolved.currentDriverName = resolved.currentDriverObj && resolved.currentDriverObj.stUser
            ? ((resolved.currentDriverObj.stUser.firstName || '') + ' ' + (resolved.currentDriverObj.stUser.lastName || '')).trim()
            : ''
          return resolved
        })

        const withOwnerNames = await this.enrichOwnerAccountNames(mappedProfiles)
        this.transportationProfiles = withOwnerNames

        if (this.selectedProfile && this.selectedProfile.id) {
          const exists = this.transportationProfiles.find((item) => item.id === this.selectedProfile.id)
          if (!exists) {
            this.transportationProfiles = [this.selectedProfile].concat(this.transportationProfiles)
          }
        }
      } catch (err) {
        addAlert({
          message: `Error in vehicle search: ${err && err.error ? err.error.message : (err && err.message ? err.message : JSON.stringify(err))}`,
          type: 'error'
        })
      } finally {
        this.isLoading = false
      }
    }
  }
}
